import React, { useEffect, useState, useRef, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import useRecommendations from "../../hooks/useRecommendations";
import usePrefetch from "../../hooks/usePrefetch";
import { useIntersectionObserverOnce } from "../../hooks/useIntersectionObserver";
import RecommendationCard from "./RecommendationCard";
import RecommendationCardSkeleton from "./RecommendationCardSkeleton";
import RecommendationFallback from "./RecommendationFallback";
import ErrorBoundary from "../ErrorBoundary";
import { generatePhoneDetailUrl } from "../../utils/slugUtils";
import { Phone } from "../../api/phones";

// Define the props interface for the SmartRecommendations component
interface SmartRecommendationsProps {
  phoneSlug: string;
  className?: string;
}

/**
 * SmartRecommendations component displays intelligent phone recommendations
 * with comprehensive error handling and fallbacks
 *
 * Accessibility features:
 * - Keyboard navigation between recommendation cards
 * - ARIA labels and roles for screen readers
 * - Focus management for interactive elements
 * - WCAG AA color contrast compliance
 */
const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({
  phoneSlug,
  className = "",
}) => {
  // Use the recommendations hook to manage data fetching and state
  const {
    recommendations,
    loading,
    error,
    isNetworkError,
    isRetrying,
    retryCount,
    refetch,
    retry,
    resetError,
  } = useRecommendations(phoneSlug);

  // Use the prefetch hook to prefetch phone details on hover
  const { prefetchPhone } = usePrefetch();

  // Use navigate for handling card clicks
  const navigate = useNavigate();

  // Track if we've attempted to load recommendations
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  // Track the currently focused card index for keyboard navigation
  const [focusedCardIndex, setFocusedCardIndex] = useState(-1);

  // Reference to the container for keyboard navigation
  const containerRef = useRef<HTMLDivElement>(null);

  // References to individual card elements for focus management
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Use the intersection observer hook to detect when recommendations come into view
  const { ref: recommendationsRef, isIntersecting } =
    useIntersectionObserverOnce<HTMLDivElement>({
      threshold: 0.1, // Trigger when at least 10% of the element is visible
      rootMargin: "200px", // Start loading a bit before the element comes into view
    });

  // Fetch recommendations when they come into view
  useEffect(() => {
    if (isIntersecting && !loading && !hasAttemptedLoad) {
      refetch();
      setHasAttemptedLoad(true);
    }
  }, [isIntersecting, loading, hasAttemptedLoad, refetch]);

  // Reset card refs when recommendations change
  useEffect(() => {
    if (recommendations.length > 0) {
      cardRefs.current = cardRefs.current.slice(0, recommendations.length);
    }
  }, [recommendations]);

  // Handle retry when there's an error
  const handleRetry = () => {
    retry(); // Use the enhanced retry function with reset
  };

  // Handle card click to navigate to phone details
  const handleCardClick = (phone: Phone) => {
    // Skip navigation for invalid phones
    if (!phone || !phone.id) return;
    navigate(generatePhoneDetailUrl(phone));
  };

  // Handle card hover to prefetch phone details
  const handleCardHover = (slug: string) => {
    // Skip prefetching for invalid slugs
    if (!slug) return;
    prefetchPhone(slug);
  };

  // Handle keyboard navigation between cards
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!recommendations.length) return;

    // Only handle arrow keys
    if (
      ![
        "ArrowRight",
        "ArrowLeft",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;

    e.preventDefault(); // Prevent page scrolling

    let newIndex = focusedCardIndex;
    const lastIndex = recommendations.length - 1;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        newIndex = focusedCardIndex < lastIndex ? focusedCardIndex + 1 : 0;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        newIndex = focusedCardIndex > 0 ? focusedCardIndex - 1 : lastIndex;
        break;
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = lastIndex;
        break;
    }

    // Update focused index and focus the card
    setFocusedCardIndex(newIndex);
    cardRefs.current[newIndex]?.focus();
  };

  // Check if error message indicates invalid phone ID
  const isInvalidPhoneSlug = error && typeof error === "string" && error.includes("Invalid phone slug");

  // Render the error boundary fallback UI
  const renderErrorBoundaryFallback = (
    error: Error,
    resetErrorBoundary: () => void
  ) => {
    return (
      <div
        className={`rounded-2xl shadow p-4 border bg-white border-brand dark:bg-gray-900 dark:border-gray-700 ${className}`}
        role="region"
        aria-label="Smart Recommendations Error"
      >
        <div className="font-semibold mb-2 flex justify-between items-center text-brand">
          Smart Recommendations
        </div>
        <RecommendationFallback
          error={error}
          resetError={() => {
            resetErrorBoundary();
            resetError();
          }}
          retry={handleRetry}
          isInvalidPhoneSlug={isInvalidPhoneSlug}
        />
      </div>
    );
  };

  // Filter out recommendations with invalid phone IDs or null phones
  const validRecommendations = recommendations.filter(
    (rec) => rec && rec.phone && rec.phone.id && rec.phone.id > 0
  );

  return (
    <ErrorBoundary fallback={renderErrorBoundaryFallback}>
      <div
        ref={recommendationsRef}
        className={`rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 border bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700 ${className}`}
        role="region"
        aria-labelledby="recommendations-heading"
      >
        <div className="mb-3 md:mb-4 flex flex-col justify-between items-start">
          <h2
            id="recommendations-heading"
            className="font-bold text-base md:text-lg md:text-xl flex items-center gap-1 md:gap-2 text-gray-900 dark:text-gray-100"
            style={{ fontFamily: "'Hind Siliguri', sans-serif" }}
          >
            <span role="img" aria-label="AI">🔁</span> আরও বিকল্প ফোন
          </h2>
          <h6 style={{ fontFamily: "'Hind Siliguri', sans-serif" }}>দাম, Performance ও Features বিবেচনায় বাছাই করা ফোনগুলো</h6>
        </div>

        {/* Loading state with skeleton cards */}
        {loading && (
          <div className="flex flex-nowrap gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible pb-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="
                  min-w-[150px] flex-shrink-0
                  md:min-w-0 md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(25%-1rem)]
                "
              >
                <RecommendationCardSkeleton />
              </div>
            ))}
          </div>
        )}

        {/* Error state with network detection and invalid phone ID detection */}
        {error && !loading && (
          <RecommendationFallback
            error={error}
            retry={handleRetry}
            isNetworkError={isNetworkError}
            isInvalidPhoneSlug={isInvalidPhoneSlug}
          />
        )}

        {/* Empty state with graceful degradation */}
        {!loading &&
          !error &&
          hasAttemptedLoad &&
          validRecommendations.length === 0 && (
            <RecommendationFallback
              error={null}
              retry={handleRetry}
              noRecommendations={true}
            />
          )}

        {/* Recommendations display - Responsive Layout */}
        {!loading && !error && validRecommendations.length > 0 && (
          <div
            ref={containerRef}
            className="flex flex-nowrap md:flex-wrap gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent"
            role="region"
            aria-label="Phone recommendations"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {validRecommendations.map((recommendation, index) => (
              <div
                key={`recommendation-${recommendation?.phone?.slug || index}-${index}`}
                className="
                  min-w-[150px] flex-shrink-0
                  md:min-w-0 md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)] xl:w-[calc(25%-0.75rem)]
                "
              >
                <RecommendationCard
                  ref={(el) => (cardRefs.current[index] = el)}
                  phone={recommendation?.phone || {
                    id: 0,
                    brand: "Unknown",
                    name: "Unknown Phone",
                    model: "Unknown Model",
                    price: "",
                    url: "",
                    slug: ""
                  }}
                  highlights={recommendation?.highlights || []}
                  badges={recommendation?.badges || []}
                  similarityScore={recommendation?.similarityScore || 0}
                  onClick={handleCardClick}
                  onMouseEnter={() => recommendation?.phone?.slug && handleCardHover(recommendation.phone.slug)}
                  index={index}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default SmartRecommendations;